import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Percent, Save, AlertTriangle, Mail, Bot, CreditCard, Flag, Building2, User, FileText, Heart, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function SystemConfiguration() {
  const [configs, setConfigs] = useState({});
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeSection, setActiveSection] = useState('features');

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      const data = await api.entities.SystemConfig.list();
      const configMap = {};
      const valueMap = {};
      data.forEach(c => { 
        configMap[c.config_key] = c;
        // Extract the actual value from the object wrapper
        valueMap[c.config_key] = c.value?.value !== undefined ? c.value.value : c.value;
      });
      setConfigs(configMap);
      setFormData(valueMap);
      
      // Seed defaults if no configs exist
      if (data.length === 0) {
        await seedDefaultConfigs();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const seedDefaultConfigs = async () => {
    const defaults = [
      { config_key: 'allow_individual_registration', config_type: 'system', name: 'Allow Individual Registration', value: { value: true } },
      { config_key: 'allow_company_registration', config_type: 'system', name: 'Allow Company Registration', value: { value: true } },
      { config_key: 'stripe_enabled', config_type: 'system', name: 'Stripe Enabled', value: { value: false } },
      { config_key: 'ai_chat_enabled', config_type: 'system', name: 'AI Chat Enabled', value: { value: true } },
      { config_key: 'claims_enabled', config_type: 'system', name: 'Claims Enabled', value: { value: true } },
      { config_key: 'qle_enabled', config_type: 'system', name: 'QLE Enabled', value: { value: true } },
      { config_key: 'api_ninjas_key', config_type: 'system', name: 'API Ninjas Key', value: { value: '' } },
      { config_key: 'maintenance_mode_enabled', config_type: 'system', name: 'Maintenance Mode', value: { value: false } },
      { config_key: 'maintenance_mode_message', config_type: 'system', name: 'Maintenance Message', value: { value: 'We are currently performing scheduled maintenance. We will be back shortly.' } },
      { config_key: 'maintenance_mode_end_time', config_type: 'system', name: 'Maintenance End Time', value: { value: '' } }
    ];

    try {
      await Promise.all(defaults.map(config => api.entities.SystemConfig.create(config)));
      toast.success('Default configurations seeded');
      loadConfigs();
    } catch (err) {
      console.error('Seeding failed:', err);
    }
  };

  const updateValue = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const getValue = (key, defaultValue) => {
    return formData[key] !== undefined ? formData[key] : defaultValue;
  };

  const saveAllChanges = async () => {
    setSaving(true);
    try {
      const promises = Object.entries(formData).map(([key, value]) => {
        const existing = configs[key];
        // Wrap value in object structure as required by SystemConfig entity
        const wrappedValue = { value };
        
        if (existing) {
          return api.entities.SystemConfig.update(existing.id, { value: wrappedValue });
        } else {
          return api.entities.SystemConfig.create({
            config_key: key,
            config_type: 'system',
            name: key,
            value: wrappedValue
          });
        }
      });
      
      await Promise.all(promises);
      toast.success('All configurations saved');
      setHasChanges(false);
      loadConfigs();
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save configurations');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>;
  }

  const hasNoConfigs = Object.keys(configs).length === 0;

  const sections = [
    { id: 'features', label: 'Feature Flags', icon: Flag },
    { id: 'maintenance', label: 'Maintenance Mode', icon: Wrench },
    { id: 'stripe', label: 'Stripe', icon: CreditCard },
    { id: 'smtp', label: 'Email (SMTP)', icon: Mail },
    { id: 'ai', label: 'AI Assistant', icon: Bot },
    { id: 'tax', label: 'Tax Rates', icon: Percent }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">System Configuration</h1>
          <p className="text-muted-foreground mt-1">Global SaaS settings - applies to all companies</p>
        </div>
        {hasChanges && (
          <Button
            onClick={saveAllChanges}
            disabled={saving}
            className="self-start sm:self-auto bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
          >
            {saving ? 'Saving...' : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Sidebar Navigation */}
        <div className="col-span-full lg:col-span-3">
          <Card className="sticky top-0 z-10 lg:top-6">
            <CardContent className="p-3">
              <nav className="flex overflow-x-auto gap-1 pb-1 lg:flex-col lg:overflow-x-visible lg:space-y-1 lg:pb-0">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`flex-shrink-0 flex items-center gap-2 px-3 py-2.5 rounded-lg transition-all text-left lg:w-full lg:gap-3 ${
                        activeSection === section.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm font-medium whitespace-nowrap">{section.label}</span>
                    </button>
                  );
                })}
              </nav>
            </CardContent>
          </Card>
        </div>

        {/* Content Area */}
        <div className="col-span-full lg:col-span-9">
          <div className="space-y-6">

        {activeSection === 'tax' && (
          <div className="space-y-6">
          <Alert>
            <Percent className="h-4 w-4" />
            <AlertDescription>
              Tax rates are managed in the <strong>Tax Rate Management</strong> page. These settings are reference values used in calculations.
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="w-5 h-5 text-emerald-500" />
                API Ninjas Income Tax API
              </CardTitle>
              <CardDescription>Store the API key used to import tax rates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>API Ninjas Key</Label>
                <Input
                  type="password"
                  value={getValue('api_ninjas_key', '')}
                  onChange={(e) => updateValue('api_ninjas_key', e.target.value)}
                  placeholder="YOUR_API_KEY"
                />
                <p className="text-xs text-muted-foreground">
                  Used by the Tax Rate Management importer. Get a key at
                  <a className="text-emerald-600 hover:underline ml-1" href="https://api-ninjas.com/api/incometax" target="_blank" rel="noreferrer">api-ninjas.com/api/incometax</a>.
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="w-5 h-5 text-purple-500" />
                Federal Tax Rates (Reference)
              </CardTitle>
              <CardDescription>View current federal tax rates - manage full brackets in Tax Rate Management</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-secondary/50 border border-border rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Social Security Rate</span>
                  <span className="font-medium text-foreground">6.2%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Medicare Rate</span>
                  <span className="font-medium text-foreground">1.45%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Additional Medicare Rate</span>
                  <span className="font-medium text-foreground">0.9%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">SS Wage Base Limit (2025)</span>
                  <span className="font-medium text-foreground">$168,600</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                To modify tax brackets and state rates, go to <strong>Tax Rate Management</strong> in the navigation menu.
              </p>
            </CardContent>
          </Card>
          </div>
        )}

        {activeSection === 'smtp' && (
          <div className="space-y-6">
          <Alert>
            <Mail className="h-4 w-4" />
            <AlertDescription>
              Configure SMTP to send invitation emails to new employees. Base44 cannot send emails to users not in the system, so SMTP is required for employee invitations.
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-500" />
                SMTP Configuration
              </CardTitle>
              <CardDescription>Configure external email service for sending invitations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Select 
                    value={getValue('smtp_provider', 'custom')} 
                    onValueChange={(v) => {
                      updateValue('smtp_provider', v);
                      // Pre-fill based on provider
                      if (v === 'sendgrid' || v === 'mailgun' || v === 'ses') {
                        updateValue('email_method', 'api');
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Custom SMTP</SelectItem>
                      <SelectItem value="sendgrid">SendGrid</SelectItem>
                      <SelectItem value="mailgun">Mailgun</SelectItem>
                      <SelectItem value="ses">Amazon SES</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Method</Label>
                  <Select 
                    value={getValue('email_method', 'smtp')} 
                    onValueChange={(v) => updateValue('email_method', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="smtp">SMTP</SelectItem>
                      {['sendgrid', 'mailgun', 'ses'].includes(getValue('smtp_provider', 'custom')) && (
                        <SelectItem value="api">API (Recommended)</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {getValue('email_method', 'smtp') === 'smtp' && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>SMTP Host</Label>
                    <Input
                      value={getValue('smtp_host', '')}
                      onChange={(e) => updateValue('smtp_host', e.target.value)}
                      placeholder="smtp.example.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>SMTP Port</Label>
                    <Input
                      type="number"
                      value={getValue('smtp_port', 587)}
                      onChange={(e) => updateValue('smtp_port', parseInt(e.target.value))}
                      placeholder="587"
                    />
                  </div>
                </div>
              )}

              {getValue('email_method', 'smtp') === 'api' ? (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>API Key</Label>
                    <Input
                      type="password"
                      value={getValue('api_key', '')}
                      onChange={(e) => updateValue('api_key', e.target.value)}
                      placeholder={
                        getValue('smtp_provider', 'custom') === 'sendgrid' ? 'SG.xxxxxxxxxx' :
                        getValue('smtp_provider', 'custom') === 'mailgun' ? 'key-xxxxxxxxxx' :
                        getValue('smtp_provider', 'custom') === 'ses' ? 'AWS Access Key ID' :
                        'Your API Key'
                      }
                    />
                  </div>

                  {getValue('smtp_provider', 'custom') === 'ses' && (
                    <div className="space-y-2">
                      <Label>AWS Secret Access Key</Label>
                      <Input
                        type="password"
                        value={getValue('api_secret', '')}
                        onChange={(e) => updateValue('api_secret', e.target.value)}
                        placeholder="AWS Secret Access Key"
                      />
                    </div>
                  )}

                  {getValue('smtp_provider', 'custom') === 'mailgun' && (
                    <div className="space-y-2">
                      <Label>Domain</Label>
                      <Input
                        value={getValue('mailgun_domain', '')}
                        onChange={(e) => updateValue('mailgun_domain', e.target.value)}
                        placeholder="mg.yourdomain.com"
                      />
                    </div>
                  )}

                  {getValue('smtp_provider', 'custom') === 'ses' && (
                    <div className="space-y-2">
                      <Label>AWS Region</Label>
                      <Input
                        value={getValue('aws_region', 'us-east-1')}
                        onChange={(e) => updateValue('aws_region', e.target.value)}
                        placeholder="us-east-1"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Username</Label>
                    <Input
                      value={getValue('smtp_username', '')}
                      onChange={(e) => updateValue('smtp_username', e.target.value)}
                      placeholder="your-username"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Password</Label>
                    <Input
                      type="password"
                      value={getValue('smtp_password', '')}
                      onChange={(e) => updateValue('smtp_password', e.target.value)}
                      placeholder="your-password"
                    />
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Email</Label>
                  <Input
                    type="email"
                    value={getValue('smtp_from_email', '')}
                    onChange={(e) => updateValue('smtp_from_email', e.target.value)}
                    placeholder="noreply@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label>From Name</Label>
                  <Input
                    value={getValue('smtp_from_name', '')}
                    onChange={(e) => updateValue('smtp_from_name', e.target.value)}
                    placeholder="Benefits Team"
                  />
                </div>
              </div>

              {getValue('smtp_provider', 'custom') !== 'custom' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-medium text-blue-900 flex items-center gap-2">
                    {getValue('smtp_provider', 'custom') === 'sendgrid' && (<><Mail className="w-4 h-4" /> SendGrid Setup</>)}
                    {getValue('smtp_provider', 'custom') === 'mailgun' && (<><Mail className="w-4 h-4" /> Mailgun Setup</>)}
                    {getValue('smtp_provider', 'custom') === 'ses' && (<><Mail className="w-4 h-4" /> Amazon SES Setup</>)}
                  </p>
                  <div className="text-xs text-blue-700 space-y-1">
                    {getValue('smtp_provider', 'custom') === 'sendgrid' && getValue('email_method', 'smtp') === 'api' && (
                      <>
                        <p>1. Create account at sendgrid.com</p>
                        <p>2. Go to Settings â†’ API Keys â†’ Create API Key</p>
                        <p>3. Choose "Full Access" and copy the key</p>
                        <p>4. Paste the API key in the field above</p>
                      </>
                    )}
                    {getValue('smtp_provider', 'custom') === 'mailgun' && getValue('email_method', 'smtp') === 'api' && (
                      <>
                        <p>1. Create account at mailgun.com</p>
                        <p>2. Go to Sending â†’ Overview â†’ API Keys</p>
                        <p>3. Copy your Private API key</p>
                        <p>4. Enter your verified domain (e.g., mg.yourdomain.com)</p>
                      </>
                    )}
                    {getValue('smtp_provider', 'custom') === 'ses' && getValue('email_method', 'smtp') === 'api' && (
                      <>
                        <p>1. Go to AWS IAM Console</p>
                        <p>2. Create user with AmazonSESFullAccess policy</p>
                        <p>3. Create access key and secret</p>
                        <p>4. Verify your sender email/domain in SES</p>
                      </>
                    )}
                    {getValue('email_method', 'smtp') === 'smtp' && (
                      <>
                        <p>Using SMTP method - configure host, port, username, and password above</p>
                        <p>Note: API method is recommended for better deliverability and features</p>
                      </>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          </div>
        )}

        {activeSection === 'ai' && (
          <div className="space-y-6">
          <Alert>
            <Bot className="h-4 w-4" />
            <AlertDescription>
              Configure VAPI for AI assistant with voice and chat capabilities.
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="w-5 h-5 text-purple-500" />
                VAPI Configuration
              </CardTitle>
              <CardDescription>Configure VAPI for voice and chat AI capabilities</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                    <Label>VAPI Public Key</Label>
                    <Input
                      type="password"
                      value={getValue('vapi_public_key', '')}
                      onChange={(e) => updateValue('vapi_public_key', e.target.value)}
                      placeholder="pk_xxxxxxxxxx"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>VAPI Private Key</Label>
                    <Input
                      type="password"
                      value={getValue('vapi_private_key', '')}
                      onChange={(e) => updateValue('vapi_private_key', e.target.value)}
                      placeholder="sk_xxxxxxxxxx"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>VAPI Agent ID</Label>
                    <Input
                      value={getValue('vapi_agent_id', '')}
                      onChange={(e) => updateValue('vapi_agent_id', e.target.value)}
                      placeholder="agent_xxxxxxxxxx"
                    />
                    <p className="text-xs text-slate-500">
                      The ID of your VAPI assistant/agent to use for conversations
                    </p>
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-2">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100 flex items-center gap-2"><Bot className="w-4 h-4" /> VAPI Setup</p>
                    <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                      <p>1. Create account at vapi.ai</p>
                      <p>2. Go to Dashboard â†’ API Keys</p>
                      <p>3. Copy your Public and Private keys</p>
                      <p>4. Create or select an assistant and copy its Agent ID</p>
                      <p>5. VAPI will handle both voice and text chat interactions</p>
                    </div>
                  </div>
            </CardContent>
          </Card>
          </div>
        )}

        {activeSection === 'features' && (
          <div className="space-y-6">
            <Alert>
              <Flag className="h-4 w-4" />
              <AlertDescription>
                Control which features are enabled or disabled across the entire platform. These affect all companies.
              </AlertDescription>
            </Alert>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Flag className="w-5 h-5 text-indigo-500" />
                  Feature Flags
                </CardTitle>
                <CardDescription>Enable or disable features across the application</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-start sm:items-center justify-between p-4 border border-border rounded-lg hover:bg-accent transition-colors gap-3">
                    <div className="flex-1">
                      <p className="font-medium flex items-center gap-2"><Building2 className="w-4 h-4 text-muted-foreground" /> Company Registration</p>
                      <p className="text-sm text-muted-foreground">Allow new companies to sign up on the landing page</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={getValue('allow_company_registration', true) === true}
                      onChange={(e) => updateValue('allow_company_registration', e.target.checked)}
                      className="h-5 w-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="flex items-start sm:items-center justify-between p-4 border border-border rounded-lg hover:bg-accent transition-colors gap-3">
                    <div className="flex-1">
                      <p className="font-medium flex items-center gap-2"><User className="w-4 h-4 text-muted-foreground" /> Individual Registration</p>
                      <p className="text-sm text-muted-foreground">Allow employees to self-register with invite codes</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={getValue('allow_individual_registration', true) === true}
                      onChange={(e) => updateValue('allow_individual_registration', e.target.checked)}
                      className="h-5 w-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="flex items-start sm:items-center justify-between p-4 border border-border rounded-lg hover:bg-accent transition-colors gap-3">
                    <div className="flex-1">
                      <p className="font-medium flex items-center gap-2"><CreditCard className="w-4 h-4 text-muted-foreground" /> Stripe Payments</p>
                      <p className="text-sm text-muted-foreground">Enable subscription billing for companies</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={getValue('stripe_enabled', false) === true}
                      onChange={(e) => updateValue('stripe_enabled', e.target.checked)}
                      className="h-5 w-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="flex items-start sm:items-center justify-between p-4 border border-border rounded-lg hover:bg-accent transition-colors gap-3">
                    <div className="flex-1">
                      <p className="font-medium flex items-center gap-2"><Bot className="w-4 h-4 text-muted-foreground" /> AI Chat Assistant</p>
                      <p className="text-sm text-muted-foreground">Enable AI chat widget for all users</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={getValue('ai_chat_enabled', true) === true}
                      onChange={(e) => updateValue('ai_chat_enabled', e.target.checked)}
                      className="h-5 w-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="flex items-start sm:items-center justify-between p-4 border border-border rounded-lg hover:bg-accent transition-colors gap-3">
                    <div className="flex-1">
                      <p className="font-medium flex items-center gap-2"><FileText className="w-4 h-4 text-muted-foreground" /> Claims Management</p>
                      <p className="text-sm text-muted-foreground">Allow employees to submit and track claims</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={getValue('claims_enabled', true) === true}
                      onChange={(e) => updateValue('claims_enabled', e.target.checked)}
                      className="h-5 w-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="flex items-start sm:items-center justify-between p-4 border border-border rounded-lg hover:bg-accent transition-colors gap-3">
                    <div className="flex-1">
                      <p className="font-medium flex items-center gap-2"><Heart className="w-4 h-4 text-muted-foreground" /> Qualifying Life Events</p>
                      <p className="text-sm text-muted-foreground">Enable mid-year enrollment changes via QLEs</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={getValue('qle_enabled', true) === true}
                      onChange={(e) => updateValue('qle_enabled', e.target.checked)}
                      className="h-5 w-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeSection === 'maintenance' && (
          <div className="space-y-6">
            <Alert>
              <Wrench className="h-4 w-4" />
              <AlertDescription>
                Enable maintenance mode to restrict site access to administrators only. Users will see a professional maintenance page with your custom message.
              </AlertDescription>
            </Alert>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-orange-500" />
                  Maintenance Mode Settings
                </CardTitle>
                <CardDescription>Control site access during maintenance periods</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start sm:items-center justify-between p-4 border rounded-lg gap-3">
                  <div className="flex-1">
                    <p className="font-medium flex items-center gap-2">
                      <Wrench className="w-4 h-4 text-muted-foreground" /> 
                      Enable Maintenance Mode
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Only administrators will be able to access the site
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={getValue('maintenance_mode_enabled', false) === true}
                      onChange={(e) => updateValue('maintenance_mode_enabled', e.target.checked)}
                      className="h-5 w-5 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                    />
                  </div>
                </div>

                <div>
                  <Label>Maintenance Message</Label>
                  <textarea
                    value={getValue('maintenance_mode_message', 'We are currently performing scheduled maintenance. We will be back shortly.')}
                    onChange={(e) => updateValue('maintenance_mode_message', e.target.value)}
                    placeholder="Enter message to display to users during maintenance..."
                    className="w-full min-h-[100px] px-3 py-2 text-sm rounded-md border border-input bg-background"
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    This message will be displayed on the maintenance page
                  </p>
                </div>

                <div>
                  <Label>Estimated End Time (Optional)</Label>
                  <Input
                    type="datetime-local"
                    value={getValue('maintenance_mode_end_time', '')}
                    onChange={(e) => updateValue('maintenance_mode_end_time', e.target.value)}
                    placeholder="Select end time..."
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    If set, a countdown timer will be displayed to users
                  </p>
                </div>

                {getValue('maintenance_mode_enabled', false) === true && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Warning:</strong> Maintenance mode is currently ACTIVE. Only administrators can access the site.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeSection === 'stripe' && (
          <div className="space-y-6">
            <Alert>
              <CreditCard className="h-4 w-4" />
              <AlertDescription>
                Configure Stripe for company subscriptions. Visit <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="underline">stripe.com</a> to create an account.
              </AlertDescription>
            </Alert>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-purple-500" />
                  Stripe API Keys
                </CardTitle>
                <CardDescription>Your Stripe credentials for payment processing</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Stripe Publishable Key</Label>
                  <Input
                    type="text"
                    value={getValue('stripe_publishable_key', '')}
                    onChange={(e) => updateValue('stripe_publishable_key', e.target.value)}
                    placeholder="pk_test_..."
                  />
                  <p className="text-xs text-muted-foreground mt-1">Used on the frontend for checkout</p>
                </div>

                <div>
                  <Label>Stripe Secret Key</Label>
                  <Input
                    type="password"
                    value={getValue('stripe_secret_key', '')}
                    onChange={(e) => updateValue('stripe_secret_key', e.target.value)}
                    placeholder="sk_test_..."
                  />
                  <p className="text-xs text-slate-500 mt-1">Encrypted and stored securely</p>
                </div>

                <div>
                  <Label>Webhook Secret</Label>
                  <Input
                    type="password"
                    value={getValue('stripe_webhook_secret', '')}
                    onChange={(e) => updateValue('stripe_webhook_secret', e.target.value)}
                    placeholder="whsec_..."
                  />
                  <p className="text-xs text-slate-500 mt-1">For verifying webhook signatures</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Billing Plans</CardTitle>
                <CardDescription>Configure your subscription pricing</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Monthly Plan Price (USD)</Label>
                    <div className="flex gap-2">
                      <span className="flex items-center px-3 border rounded-l bg-slate-50">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={getValue('stripe_monthly_price', '')}
                        onChange={(e) => updateValue('stripe_monthly_price', parseFloat(e.target.value) || 0)}
                        placeholder="49.99"
                        className="rounded-l-none"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Stripe Price ID (Monthly)</Label>
                    <Input
                      type="text"
                      value={getValue('stripe_monthly_price_id', '')}
                      onChange={(e) => updateValue('stripe_monthly_price_id', e.target.value)}
                      placeholder="price_..."
                    />
                    <p className="text-xs text-slate-500 mt-1">Create in Stripe dashboard</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Annual Plan Price (USD)</Label>
                    <div className="flex gap-2">
                      <span className="flex items-center px-3 border rounded-l bg-slate-50">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={getValue('stripe_annual_price', '')}
                        onChange={(e) => updateValue('stripe_annual_price', parseFloat(e.target.value) || 0)}
                        placeholder="499.99"
                        className="rounded-l-none"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Stripe Price ID (Annual)</Label>
                    <Input
                      type="text"
                      value={getValue('stripe_annual_price_id', '')}
                      onChange={(e) => updateValue('stripe_annual_price_id', e.target.value)}
                      placeholder="price_..."
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Free Trial Days</Label>
                    <Input
                      type="number"
                      value={getValue('stripe_trial_days', '')}
                      onChange={(e) => updateValue('stripe_trial_days', parseInt(e.target.value) || 0)}
                      placeholder="14"
                    />
                  </div>

                  <div>
                    <Label>Max Employees per Plan</Label>
                    <Input
                      type="number"
                      value={getValue('stripe_max_employees', '')}
                      onChange={(e) => updateValue('stripe_max_employees', parseInt(e.target.value) || 0)}
                      placeholder="100"
                    />
                    <p className="text-xs text-slate-500 mt-1">0 = unlimited</p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-medium text-blue-900 flex items-center gap-2"><CreditCard className="w-4 h-4" /> Setting up Stripe Plans</p>
                  <div className="text-xs text-blue-700 space-y-1">
                    <p>1. Go to Stripe Dashboard â†’ Products</p>
                    <p>2. Create a new product for your subscription</p>
                    <p>3. Add pricing (monthly and/or annual)</p>
                    <p>4. Copy the Price IDs and paste them above</p>
                    <p>5. Enable "Stripe Payments" in Feature Flags section</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
}